// action - state management
import { createSlice } from '@reduxjs/toolkit';
import { UIStore } from 'types/ui-general';

const initialState: UIStore = {
  lastSaved: null,
  breadCrumbs: [],
  gridMode: false,
  portalModal: false,
  showRichText: true,
  currentSlug: '',
  customPageTitle: null,
  mailingListsOpen: false,
  update: null,
  panelStates: {
    detailsCoreFields: true,
    detailsAttire: false,
    detailsSound: false,
    detailsCustomFields: true
  },
  segmentGridCollapse: {},
  detailsView: {
    columnVisibility: {}
  }
};

// ===========================|| DIALOG REDUCER ||=========================== //

const ui = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLastSaved(state, action) {
      state.lastSaved = action.payload;
    },
    setBreadcrumbs(state, action) {
      state.breadCrumbs = action.payload;
    },
    setGridMode(state, action) {
      state.gridMode = action.payload;
    },
    setPortalModal(state, action) {
      state.portalModal = action.payload;
    },
    setShowRichText(state, action) {
      state.showRichText = action.payload;
    },
    setCurrentSlug(state, action) {
      state.currentSlug = action.payload;
    },
    setCustomPageTitle(state, action) {
      state.customPageTitle = action.payload;
    },
    setMailingListsOpen(state, action) {
      state.mailingListsOpen = action.payload;
    },
    setUpdate(state, action) {
      state.update = action.payload;
    },
    setPanelState(state, action: { payload: { panel: keyof UIStore['panelStates']; expanded: boolean } }) {
      state.panelStates[action.payload.panel] = action.payload.expanded;
    },
    setSegmentGridCollapse(state, action: { payload: { key: string; expanded: boolean } }) {
      const { key, expanded } = action.payload;
      // Default is expanded, so only persist the exception (collapsed grids) and drop the key on
      // re-expand — the map only ever lists what's currently collapsed.
      if (expanded) {
        delete state.segmentGridCollapse[key];
        return;
      }
      state.segmentGridCollapse[key] = false;
      // Hard cap: keys are only added when a grid is currently expanded, so each is a fresh insertion
      // at the end → JS insertion order = FIFO recency. Evict the oldest until within the cap; the
      // just-added key (at the end) always survives.
      const keys = Object.keys(state.segmentGridCollapse);
      const MAX = 25;
      for (let i = 0; i < keys.length - MAX; i++) {
        delete state.segmentGridCollapse[keys[i]];
      }
    },
    setDetailsColumnVisibility(state, action: { payload: Record<string, boolean> }) {
      state.detailsView.columnVisibility = action.payload;
    }
  }
});

export default ui.reducer;

export const {
  setLastSaved,
  setBreadcrumbs,
  setGridMode,
  setPortalModal,
  setShowRichText,
  setCurrentSlug,
  setCustomPageTitle,
  setMailingListsOpen,
  setUpdate,
  setPanelState,
  setSegmentGridCollapse,
  setDetailsColumnVisibility
} = ui.actions;
